package com.shadowings.gero.util

/**
 * Returns the current time in milliseconds since epoch.
 * Platform-specific implementation.
 */
expect fun currentTimeMillis(): Long

/**
 * Returns the current date as YYYYMMDD integer (e.g., 20260306 for March 6, 2026).
 * This is used to check if it's a new calendar day, regardless of timezone.
 */
expect fun currentDateAsInt(): Int

fun dateIntToEpochDays(dateAsInt: Int): Int {
    val year = dateAsInt / 10000
    val month = (dateAsInt % 10000) / 100
    val day = dateAsInt % 100
    var y = year
    var m = month
    if (m <= 2) {
        y -= 1
        m += 12
    }
    val era = (if (y >= 0) y else y - 399) / 400
    val yoe = y - era * 400
    val doy = (153 * (m - 3) + 2) / 5 + day - 1
    val doe = yoe * 365 + yoe / 4 - yoe / 100 + doy
    return era * 146097 + doe - 719468
}

fun isConsecutiveDay(prev: Int, next: Int): Boolean {
    if (prev <= 0 || next <= 0) return false
    return dateIntToEpochDays(next) - dateIntToEpochDays(prev) == 1
}

fun epochDaysToDateInt(epochDays: Int): Int {
    val z = epochDays + 719468
    val era = (if (z >= 0) z else z - 146096) / 146097
    val doe = z - era * 146097
    val yoe = (doe - doe / 1460 + doe / 36524 - doe / 146096) / 365
    var y = yoe + era * 400
    val doy = doe - (365 * yoe + yoe / 4 - yoe / 100)
    val mp = (5 * doy + 2) / 153
    val d = doy - (153 * mp + 2) / 5 + 1
    val m = mp + (if (mp < 10) 3 else -9)
    y += (if (m <= 2) 1 else 0)
    return y * 10000 + m * 100 + d
}

fun dateIntToString(dateAsInt: Int): String {
    if (dateAsInt <= 0) return ""
    val year = dateAsInt / 10000
    val month = (dateAsInt % 10000) / 100
    val day = dateAsInt % 100
    val mStr = if (month < 10) "0$month" else "$month"
    val dStr = if (day < 10) "0$day" else "$day"
    return "$year-$mStr-$dStr"
}

fun dateStringToDateInt(dateStr: String): Int {
    if (dateStr.isBlank()) return 0
    val parts = dateStr.split("-")
    if (parts.size != 3) return 0
    val y = parts[0].toIntOrNull() ?: return 0
    val m = parts[1].toIntOrNull() ?: return 0
    val d = parts[2].toIntOrNull() ?: return 0
    return y * 10000 + m * 100 + d
}

fun dateStringToEpochDays(dateStr: String): Int {
    val intVal = dateStringToDateInt(dateStr)
    return if (intVal > 0) dateIntToEpochDays(intVal) else 0
}

fun epochDaysToDateString(epochDays: Int): String {
    return dateIntToString(epochDaysToDateInt(epochDays))
}

fun currentDateAsString(): String {
    return dateIntToString(currentDateAsInt())
}

/**
 * Calculates the current consecutive daily streak from a list of completed quiz dates ("yyyy-MM-dd").
 *
 * Rules:
 * - If today is present: count consecutive days backwards starting from today.
 * - If today is missing but yesterday is present: count consecutive days backwards starting from yesterday.
 * - If neither today nor yesterday is present: streak is 0.
 */
fun calculateStreak(
    completedQuizDates: List<String>,
    todayDateStr: String = currentDateAsString(),
): Int {
    if (completedQuizDates.isEmpty() || todayDateStr.isBlank()) return 0

    val epochSet = completedQuizDates
        .mapNotNull { dateStr ->
            val epoch = dateStringToEpochDays(dateStr)
            if (epoch > 0) epoch else null
        }
        .toSet()

    if (epochSet.isEmpty()) return 0

    val todayEpoch = dateStringToEpochDays(todayDateStr)
    if (todayEpoch <= 0) return 0

    val startEpoch = when {
        todayEpoch in epochSet -> todayEpoch
        (todayEpoch - 1) in epochSet -> todayEpoch - 1
        else -> return 0
    }

    var count = 0
    var checkEpoch = startEpoch
    while (checkEpoch in epochSet) {
        count++
        checkEpoch--
    }

    return count
}


